import { useState, useEffect, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, STORAGE_KEY } from '../services/supabaseClient';
import { isEmailAllowed } from '../services/emailAllowlistService';

export type AuthUser = User | null;

type AuthState = {
  user: AuthUser;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: AuthError | null;
};

type UseAuthReturn = AuthState & {
  signInWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
};

// Use the storage key from supabaseClient
const AUTH_STORAGE_KEY = STORAGE_KEY;

// Singleton state for auth - shared across all hook instances
const globalAuthState: {
  user: AuthUser;
  session: Session | null;
  isLoading: boolean;
  error: AuthError | null;
  initialized: boolean;
  listeners: Set<() => void>;
} = {
  user: null,
  session: null,
  isLoading: true,
  error: null,
  initialized: false,
  listeners: new Set(),
};

// Initialize auth once globally
let initPromise: Promise<void> | null = null;

const initializeAuth = () => {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    if (globalAuthState.initialized) return;
    globalAuthState.initialized = true;

    console.log('[useAuth] Initializing auth (singleton)...');

    // Debug: Check what's in localStorage
    try {
      const storedSession = localStorage.getItem(AUTH_STORAGE_KEY);
      console.log('[useAuth] localStorage check:', {
        key: AUTH_STORAGE_KEY,
        hasSession: storedSession ? 'YES' : 'NO',
        sessionPreview: storedSession
          ? storedSession.substring(0, 50) + '...'
          : 'null',
      });
    } catch (e) {
      console.warn('[useAuth] Could not check localStorage:', e);
    }

    // Set up auth state listener - this is the ONLY source of truth
    // onAuthStateChange will fire immediately with INITIAL_SESSION if there's a stored session
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log(
        '[useAuth] Auth state changed:',
        event,
        newSession?.user?.email
      );

      // Update state based on the event
      globalAuthState.session = newSession;
      globalAuthState.user = newSession?.user ?? null;
      globalAuthState.isLoading = false;
      globalAuthState.error = null;
      globalAuthState.listeners.forEach((listener) => listener());
    });

    // Store subscription for potential cleanup (not used currently but good practice)
    (globalAuthState as { subscription?: typeof subscription }).subscription =
      subscription;

    // Give Supabase a moment to fire INITIAL_SESSION event
    // If no event fires within 100ms, check manually
    await new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {
        if (!globalAuthState.isLoading) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);

      // Fallback: manually check session after 2 seconds if no event fired
      setTimeout(async () => {
        clearInterval(checkInterval);
        if (globalAuthState.isLoading) {
          console.log(
            '[useAuth] No auth event received, checking session manually...'
          );
          try {
            const { data } = await supabase.auth.getSession();
            if (data.session) {
              console.log(
                '[useAuth] Manual check found session:',
                data.session.user?.email
              );
              globalAuthState.session = data.session;
              globalAuthState.user = data.session.user ?? null;
            } else {
              console.log('[useAuth] Manual check: no session');
            }
          } catch (err) {
            console.warn('[useAuth] Manual session check failed:', err);
          }
          globalAuthState.isLoading = false;
          globalAuthState.listeners.forEach((listener) => listener());
        }
        resolve();
      }, 2000);
    });
  })();

  return initPromise;
};

export function useAuth(): UseAuthReturn {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    // Subscribe to auth state changes
    const listener = () => forceUpdate({});
    globalAuthState.listeners.add(listener);

    // Initialize auth if not already done
    initializeAuth();

    return () => {
      globalAuthState.listeners.delete(listener);
    };
  }, []);

  // Helper to update global state and notify listeners
  const updateGlobalState = useCallback(
    (updates: Partial<typeof globalAuthState>) => {
      Object.assign(globalAuthState, updates);
      globalAuthState.listeners.forEach((listener) => listener());
    },
    []
  );

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      updateGlobalState({ isLoading: true, error: null });

      try {
        const allowed = await isEmailAllowed(email);
        if (!allowed) {
          const notAllowedError = {
            message: 'Access denied. This email is not authorized to sign in.',
            name: 'AuthApiError',
            status: 403,
          } as AuthError;
          updateGlobalState({ error: notAllowedError, isLoading: false });
          throw notAllowedError;
        }

        const { data, error: signInError } =
          await supabase.auth.signInWithPassword({
            email,
            password,
          });

        if (signInError) {
          console.error('[useAuth] Email sign-in error:', signInError);
          updateGlobalState({ error: signInError, isLoading: false });
          throw signInError;
        }

        if (data.session) {
          updateGlobalState({
            session: data.session,
            user: data.user,
            isLoading: false,
          });
        }
      } catch (err) {
        console.error('[useAuth] Unexpected email sign-in error:', err);
        throw err;
      } finally {
        updateGlobalState({ isLoading: false });
      }
    },
    [updateGlobalState]
  );

  const resetPassword = useCallback(
    async (email: string) => {
      updateGlobalState({ isLoading: true, error: null });

      try {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(
          email,
          {
            redirectTo: `${window.location.origin}/reset-password`,
          }
        );

        if (resetError) {
          console.error('[useAuth] Password reset error:', resetError);
          updateGlobalState({ error: resetError, isLoading: false });
          throw resetError;
        }
      } catch (err) {
        console.error('[useAuth] Unexpected password reset error:', err);
        throw err;
      } finally {
        updateGlobalState({ isLoading: false });
      }
    },
    [updateGlobalState]
  );

  const updatePassword = useCallback(
    async (newPassword: string) => {
      updateGlobalState({ isLoading: true, error: null });

      try {
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword,
        });

        if (updateError) {
          console.error('[useAuth] Password update error:', updateError);
          updateGlobalState({ error: updateError, isLoading: false });
          throw updateError;
        }
      } catch (err) {
        console.error('[useAuth] Unexpected password update error:', err);
        throw err;
      } finally {
        updateGlobalState({ isLoading: false });
      }
    },
    [updateGlobalState]
  );

  const signOut = useCallback(async () => {
    updateGlobalState({ isLoading: true, error: null });

    try {
      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) {
        console.error('[useAuth] Sign-out error:', signOutError);
        updateGlobalState({ error: signOutError });
      }
    } catch (err) {
      console.error('[useAuth] Unexpected sign-out error:', err);
    } finally {
      // Always clear state regardless of API success
      updateGlobalState({
        user: null,
        session: null,
        isLoading: false,
      });

      // Clear any cached auth data
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      } catch {
        // Ignore storage errors
      }
    }
  }, [updateGlobalState]);

  const clearError = useCallback(() => {
    updateGlobalState({ error: null });
  }, [updateGlobalState]);

  return {
    user: globalAuthState.user,
    session: globalAuthState.session,
    isLoading: globalAuthState.isLoading,
    isAuthenticated: !!globalAuthState.user,
    error: globalAuthState.error,
    signInWithEmail,
    resetPassword,
    updatePassword,
    signOut,
    clearError,
  };
}

export default useAuth;
